'use client';
import React, { useState } from 'react';
import Header from '@/components/Header';
import BookingModal from '@/components/BookingModal';

interface Experience {
  id: string;
  name: string;
  nameEn: string;
  priceBase: number;
  priceTransport: number;
}

type Lang = 'es' | 'en';

const t = {
  es: {
    heroTag: 'Cenote Cerrado · Solo Para Ti',
    heroTitle: 'Tu Cenote',
    heroTitleAccent: 'Privado',
    heroSub: 'Una experiencia única en un cenote exclusivo, cerrado al público. Cuatro experiencias extraordinarias en el corazón de la selva.',
    heroBook: 'Reservar Ahora',
    heroExplore: 'Ver Experiencias',
    tags: ['Privado', 'Exclusivo', 'Único', 'Cenote Cerrado'],
    aboutTag: 'El Cenote',
    aboutTitle: 'Un Lugar Sagrado,',
    aboutTitleAccent: 'Solo Para Ti',
    aboutP1: 'Nuestro cenote es una joya natural de aguas cristalinas, formado hace miles de años en el corazón de la selva. A diferencia de los cenotes turísticos, este espacio permanece cerrado al público general.',
    aboutP2: 'Cada reserva garantiza acceso exclusivo para tu grupo. Sin multitudes, sin ruido, sin interrupciones. Solo tú, la naturaleza y una experiencia diseñada para ti.',
    aboutStats: ['100% Privado', '4 Experiencias', '2 Aliados Premium'],
    aboutFeatures: ['Acceso Privado', 'Aguas Cristalinas', 'Selva Virgen', 'Herencia Maya'],
    aboutAllies: 'Nuestros Aliados',
    expTag: 'Nuestras Experiencias',
    expTitle: 'Las Cuatro Experiencias',
    expSub: 'Cada experiencia está diseñada para ser única, íntima y memorable. Todas disponibles con o sin transporte.',
    expFrom: 'Desde',
    expTransport: 'Con transporte:',
    expBook: 'Reservar',
    expCustom: '¿Quieres combinar experiencias o tienes un grupo especial?',
    expCustomBtn: 'Consultar Paquete Personalizado',
    whyTag: 'Diferenciadores',
    whyTitle: '¿Por Qué Elegirnos?',
    whySub: 'No es solo un cenote. Es una experiencia diseñada para ser única, privada e inolvidable.',
    addonsTag: 'Extras',
    addonsTitle: 'Eleva Tu Experiencia',
    addonsSub: 'Personaliza tu visita con nuestros extras premium para una experiencia verdaderamente exclusiva.',
    providersTag: 'Nuestros Aliados',
    providersTitle: 'Expertos en Su Arte',
    reviewsTag: 'Testimonios',
    reviewsTitle: 'Lo Que Dicen Nuestros Huéspedes',
    softOpening: '✦ Soft Opening',
    softOpeningSub: '— Sé de los primeros en vivir esta experiencia',
    softOpeningCta: 'Reserva en Nuestro Soft Opening',
    softOpeningCtaSub: 'Sé parte de los primeros en vivir esta experiencia exclusiva. Precio especial para los primeros visitantes.',
    softOpeningBtn: 'Reservar con Precio Especial',
    faqTag: 'FAQ',
    faqTitle: 'Preguntas Frecuentes',
    footerDesc: 'Una experiencia única en un cenote exclusivo, cerrado al público. Privado, exclusivo, único.',
    footerExp: 'Experiencias',
    footerContact: 'Contacto',
    footerRights: '© 2026 Cenote Privado. Todos los derechos reservados.',
    footerTagline: 'PRIVADO · EXCLUSIVO · ÚNICO · CENOTE CERRADO'
  },
  en: {
    heroTag: 'Private Cenote · Just For You',
    heroTitle: 'Your Private',
    heroTitleAccent: 'Cenote',
    heroSub: 'A unique experience in an exclusive cenote, closed to the public. Four extraordinary experiences in the heart of the jungle.',
    heroBook: 'Book Now',
    heroExplore: 'Explore Experiences',
    tags: ['Private', 'Exclusive', 'Unique', 'Closed Cenote'],
    aboutTag: 'The Cenote',
    aboutTitle: 'A Sacred Place,',
    aboutTitleAccent: 'Just For You',
    aboutP1: 'Our cenote is a natural gem of crystal-clear waters, formed thousands of years ago in the heart of the jungle. Unlike tourist cenotes, this space remains closed to the general public.',
    aboutP2: 'Every reservation guarantees exclusive access for your group. No crowds, no noise, no interruptions. Just you, nature, and an experience designed for you.',
    aboutStats: ['100% Private', '4 Experiences', '2 Premium Partners'],
    aboutFeatures: ['Private Access', 'Crystal Waters', 'Virgin Jungle', 'Mayan Heritage'],
    aboutAllies: 'Our Partners',
    expTag: 'Our Experiences',
    expTitle: 'The Four Experiences',
    expSub: 'Each experience is designed to be unique, intimate and memorable. All available with or without transport.',
    expFrom: 'From',
    expTransport: 'With transport:',
    expBook: 'Book',
    expCustom: 'Want to combine experiences or have a special group?',
    expCustomBtn: 'Inquire About Custom Package',
    whyTag: 'Why Us',
    whyTitle: 'Why Choose Us?',
    whySub: 'This is not just a cenote. It is an experience designed to be unique, private and unforgettable.',
    addonsTag: 'Extras',
    addonsTitle: 'Elevate Your Experience',
    addonsSub: 'Personalize your visit with our premium extras for a truly exclusive experience.',
    providersTag: 'Our Partners',
    providersTitle: 'Masters of Their Craft',
    reviewsTag: 'Testimonials',
    reviewsTitle: 'What Our Guests Say',
    softOpening: '✦ Soft Opening',
    softOpeningSub: '— Be among the first to live this experience',
    softOpeningCta: 'Book During Our Soft Opening',
    softOpeningCtaSub: 'Be among the first to experience this exclusive cenote. Special pricing for early visitors.',
    softOpeningBtn: 'Book at Special Price',
    faqTag: 'FAQ',
    faqTitle: 'Frequently Asked Questions',
    footerDesc: 'A unique experience in an exclusive cenote, closed to the public. Private, exclusive, unique.',
    footerExp: 'Experiences',
    footerContact: 'Contact',
    footerRights: '© 2026 Cenote Privado. All rights reserved.',
    footerTagline: 'PRIVATE · EXCLUSIVE · UNIQUE · CLOSED CENOTE'
  }
};

const experiences = [
{
  id: 'desayuno',
  name: 'Desayuno en el Cenote',
  nameEn: 'Cenote Breakfast',
  provider: 'Copal Gastrobar',
  providerType: { es: 'Gastronomía', en: 'Gastronomy' },
  description: { es: 'Desayuno exclusivo al borde del cenote, rodeado de naturaleza virgen. Una experiencia gastronómica única al amanecer que despertará todos tus sentidos.', en: 'Exclusive breakfast at the edge of the cenote, surrounded by virgin nature. A unique gastronomic experience at dawn that will awaken all your senses.' },
  priceBase: 1944,
  priceTransport: 2344,
  image: '/assets/images/IMG_8355-1783922250924.jpg',
  alt: 'Desayuno gourmet con frutas tropicales y café junto a un cenote en la selva mexicana',
  includes: { es: ['Desayuno completo', 'Acceso exclusivo al cenote', 'Toallas disponibles'], en: ['Full breakfast', 'Exclusive cenote access', 'Towels available'] },
  badge: { es: 'Más Popular', en: 'Most Popular' }
},
{
  id: 'masaje',
  name: 'Masaje en el Cenote',
  nameEn: 'Cenote Massage',
  provider: 'Healing Spot',
  providerType: { es: 'Wellness', en: 'Wellness' },
  description: { es: 'Masaje terapéutico en plena naturaleza, con el sonido del agua del cenote. Elige entre Deep Tissue o Masaje Relajante para una experiencia de bienestar sin igual.', en: 'Therapeutic massage in the heart of nature, with the sound of the cenote water. Choose between Deep Tissue or Relaxing Massage for an unparalleled wellness experience.' },
  priceBase: 3565,
  priceTransport: 3965,
  image: '/assets/images/CC5A5080-C3BD-4746-9204-2F5EDF2D4F18-1783922379703.JPG',
  alt: 'Masaje terapéutico relajante en un entorno natural con velas y flores tropicales',
  includes: { es: ['Masaje 60 min (Deep Tissue o Relax)', 'Acceso exclusivo al cenote', 'Posibilidad de nadar', 'Batas y toallas'], en: ['60 min massage (Deep Tissue or Relax)', 'Exclusive cenote access', 'Swimming option', 'Robes and towels'] },
  badge: { es: 'Wellness', en: 'Wellness' }
},
{
  id: 'cena',
  name: 'Cena en el Cenote',
  nameEn: 'Cenote Dinner',
  provider: 'Copal Gastrobar',
  providerType: { es: 'Gastronomía', en: 'Gastronomy' },
  description: { es: 'Cena íntima bajo las estrellas junto al cenote. Menú cerrado con entrante, plato principal y bebida incluida. Carta limitada disponible aparte.', en: 'Intimate dinner under the stars by the cenote. Set menu with starter, main course and drink included. Limited à la carte available separately.' },
  priceBase: 2185,
  priceTransport: 2585,
  image: '/assets/images/IMG_6991-1783922425200.JPG',
  alt: 'Mesa elegante con cena gourmet iluminada con velas junto a un cenote de noche',
  includes: { es: ['Entrante + Principal + Bebida', 'Acceso exclusivo al cenote', 'Carta limitada disponible'], en: ['Starter + Main + Drink', 'Exclusive cenote access', 'Limited à la carte available'] },
  badge: { es: 'Romántico', en: 'Romantic' }
},
{
  id: 'ritual',
  name: 'Ritual Maya',
  nameEn: 'Mayan Ritual',
  provider: 'Healing Spot',
  providerType: { es: 'Wellness', en: 'Wellness' },
  description: { es: 'Sumérgete en la sabiduría ancestral Maya. Terapia Maya completa en un entorno sagrado. Disponible como experiencia individual o ritual de parejas.', en: 'Immerse yourself in ancestral Mayan wisdom. Complete Mayan therapy in a sacred setting. Available as an individual experience or couples ritual.' },
  priceBase: 5865,
  priceTransport: 6265,
  image: '/assets/images/IMG_6985-1783922979846.JPG',
  alt: 'Ritual de bienestar ancestral con flores, velas y elementos naturales en un cenote sagrado',
  includes: { es: ['Terapia Maya completa', 'Acceso exclusivo al cenote', 'Upgrade a ritual de parejas disponible'], en: ['Complete Mayan therapy', 'Exclusive cenote access', 'Couples ritual upgrade available'] },
  badge: { es: 'Ancestral', en: 'Ancestral' }
}];


const differentiators = {
  es: [
  { title: 'Cenote Privado', desc: 'Solo tú y tus acompañantes. Sin turistas, sin multitudes. Acceso exclusivo garantizado.' },
  { title: 'Completamente Exclusivo', desc: 'Cenote cerrado al público general. Una experiencia que no encontrarás en ningún otro lugar.' },
  { title: 'Naturaleza Pura', desc: 'Entorno natural virgen en el corazón de la selva. Aguas cristalinas de origen subterráneo.' },
  { title: 'Gastronomía Premium', desc: 'Copal Gastrobar, cocina de autor en plena naturaleza. Ingredientes locales, sabores únicos.' },
  { title: 'Wellness Ancestral', desc: 'Healing Spot, terapias con raíces mayas. Bienestar auténtico en un entorno sagrado.' },
  { title: 'Transporte Disponible', desc: 'Recogida y regreso desde tu hotel. Relájate desde el primer momento.' }],

  en: [
  { title: 'Private Cenote', desc: 'Just you and your companions. No tourists, no crowds. Guaranteed exclusive access.' },
  { title: 'Completely Exclusive', desc: 'Cenote closed to the general public. An experience you will not find anywhere else.' },
  { title: 'Pure Nature', desc: 'Virgin natural setting in the heart of the jungle. Crystal-clear underground spring waters.' },
  { title: 'Premium Gastronomy', desc: 'Copal Gastrobar, signature cuisine in nature. Local ingredients, unique flavors.' },
  { title: 'Ancestral Wellness', desc: 'Healing Spot, therapies rooted in Mayan tradition. Authentic wellness in a sacred setting.' },
  { title: 'Transport Available', desc: 'Pick-up and drop-off from your hotel. Relax from the very first moment.' }]

};

const addons = {
  es: [
  { name: 'Toallas Premium', desc: 'Toallas de algodón egipcio', price: '+$200 MXN' },
  { name: 'Batas de Baño', desc: 'Batas de lujo para tu comodidad', price: '+$300 MXN' },
  { name: 'Chancletas', desc: 'Chancletas premium antideslizantes', price: '+$100 MXN' },
  { name: 'Acceso a Nadar', desc: 'Nada en las aguas cristalinas', price: '+$150 MXN' },
  { name: 'Flores y Velas', desc: 'Decoración romántica especial', price: '+$300 MXN' },
  { name: 'Maridaje de Vinos', desc: 'Selección de vinos curada', price: '+$400 MXN' }],

  en: [
  { name: 'Premium Towels', desc: 'Egyptian cotton towels', price: '+$200 MXN' },
  { name: 'Bath Robes', desc: 'Luxury robes for your comfort', price: '+$300 MXN' },
  { name: 'Sandals', desc: 'Premium non-slip sandals', price: '+$100 MXN' },
  { name: 'Swimming Access', desc: 'Swim in the crystal waters', price: '+$150 MXN' },
  { name: 'Flowers & Candles', desc: 'Special romantic decoration', price: '+$300 MXN' },
  { name: 'Wine Pairing', desc: 'Curated wine selection', price: '+$400 MXN' }]

};

const reviews = [
{
  name: 'Valentina Reyes',
  location: 'Ciudad de México',
  rating: 5,
  text: { es: 'Una experiencia absolutamente mágica. El cenote es de otro mundo y el desayuno de Copal Gastrobar fue increíble. Totalmente privado, sin nadie más. Lo recomiendo al 100%.', en: 'An absolutely magical experience. The cenote is out of this world and the Copal Gastrobar breakfast was incredible. Completely private, no one else around. I recommend it 100%.' },
  experience: { es: 'Desayuno en el Cenote', en: 'Cenote Breakfast' },
  avatar: 'VR'
},
{
  name: 'Carlos & Ana Mendoza',
  location: 'Guadalajara',
  rating: 5,
  text: { es: 'Hicimos el Ritual Maya para nuestro aniversario. Fue una experiencia transformadora. El equipo de Healing Spot es extraordinario. El cenote es sagrado y hermoso.', en: 'We did the Mayan Ritual for our anniversary. It was a transformative experience. The Healing Spot team is extraordinary. The cenote is sacred and beautiful.' },
  experience: { es: 'Ritual Maya', en: 'Mayan Ritual' },
  avatar: 'CM'
},
{
  name: 'Sophie Laurent',
  location: 'Paris, France',
  rating: 5,
  text: { es: 'El masaje junto al cenote fue la experiencia de bienestar más única que he tenido. El sonido del agua, la selva, la privacidad... absolutamente inolvidable.', en: 'The massage by the cenote was the most unique wellness experience I\'ve ever had. The sound of the water, the jungle, the privacy... absolutely unforgettable.' },
  experience: { es: 'Masaje en el Cenote', en: 'Cenote Massage' },
  avatar: 'SL'
}];


const faqs = {
  es: [
  { q: '¿Cuántas personas pueden asistir?', a: 'El cenote es completamente privado para tu grupo. Aceptamos grupos de 1 a 8 personas para garantizar la exclusividad de la experiencia.' },
  { q: '¿Qué incluye el transporte?', a: 'El transporte incluye recogida en tu hotel y regreso al finalizar la experiencia. Cubrimos la zona hotelera y el centro.' },
  { q: '¿Con cuánta anticipación debo reservar?', a: 'Recomendamos reservar con al menos 48 horas de anticipación. Para fechas especiales o grupos grandes, sugerimos una semana.' },
  { q: '¿Puedo nadar en el cenote?', a: 'Sí, especialmente en las experiencias de masaje y ritual. Para desayuno y cena también es posible como add-on.' },
  { q: '¿Cómo realizo el pago?', a: 'El pago se realiza al confirmar la reserva. Aceptamos transferencia bancaria, tarjeta de crédito/débito y efectivo.' },
  { q: '¿Puedo personalizar mi experiencia?', a: 'Absolutamente. Contáctanos para paquetes personalizados, celebraciones especiales o combinaciones de experiencias.' }],

  en: [
  { q: 'How many people can attend?', a: 'The cenote is completely private for your group. We accept groups of 1 to 8 people to guarantee the exclusivity of the experience.' },
  { q: 'What does transport include?', a: 'Transport includes pick-up at your hotel and return at the end of the experience. We cover the hotel zone and city center.' },
  { q: 'How far in advance should I book?', a: 'We recommend booking at least 48 hours in advance. For special dates or large groups, we suggest one week.' },
  { q: 'Can I swim in the cenote?', a: 'Yes, especially in the massage and ritual experiences. For breakfast and dinner it is also possible as an add-on.' },
  { q: 'How do I pay?', a: 'Payment is made upon booking confirmation. We accept bank transfer, credit/debit card and cash.' },
  { q: 'Can I customize my experience?', a: 'Absolutely. Contact us for custom packages, special celebrations or experience combinations.' }]

};

export default function HomePage() {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [selectedExp, setSelectedExp] = useState<Experience | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [lang, setLang] = useState<Lang>('en');

  const tx = t[lang];

  const openBooking = (exp?: Experience) => {
    setSelectedExp(exp || null);
    setBookingOpen(true);
  };

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-lux-black min-h-screen font-sans">
      <Header onReservarClick={() => openBooking()} lang={lang} onLangChange={setLang} />

      {/* HERO */}
      <section id="inicio" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/assets/images/IMG_8354-1783922175229.jpg"
            alt="Vista aérea de un cenote privado con aguas cristalinas rodeado de selva tropical"
            className="w-full h-full object-cover"
            loading="eager"
            decoding="async" />
          
          <div className="absolute inset-0 bg-gradient-to-b from-lux-black/70 via-lux-black/50 to-lux-black/90" />
          {/* Grain overlay for luxury feel */}
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'0.4\'/%3E%3C/svg%3E")', backgroundRepeat: 'repeat', backgroundSize: '128px' }} />
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-3 mb-8">
            <div className="h-px w-12 bg-lux-gold/60" />
            <span className="text-lux-gold text-xs font-medium tracking-[0.3em] uppercase">{tx.heroTag}</span>
            <div className="h-px w-12 bg-lux-gold/60" />
          </div>

          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-white font-light leading-[1.05] mb-6 tracking-tight">
            {tx.heroTitle}
            <span className="block italic text-lux-pearl">{tx.heroTitleAccent}</span>
          </h1>

          <p className="text-white/60 text-base md:text-lg max-w-xl mx-auto mb-8 leading-relaxed font-light">
            {tx.heroSub}
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {tx.tags.map((tag) =>
            <span key={tag} className="border border-white/20 text-white/50 px-4 py-1.5 text-xs font-medium tracking-[0.2em] uppercase">
                {tag}
              </span>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => openBooking()}
              className="bg-white text-lux-black hover:bg-lux-pearl px-10 py-4 text-xs font-medium tracking-[0.25em] uppercase transition-all duration-300 shadow-2xl">
              
              {tx.heroBook}
            </button>
            <button
              onClick={() => scrollTo('experiencias')}
              className="border border-white/30 text-white hover:border-white/60 px-10 py-4 text-xs font-medium tracking-[0.25em] uppercase transition-all duration-300">
              
              {tx.heroExplore}
            </button>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
          <div className="w-px h-12 bg-gradient-to-b from-transparent to-white/30" />
          <svg className="w-4 h-4 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </section>

      {/* ABOUT / THE CENOTE */}
      <section id="cenote" className="py-24 md:py-32 bg-lux-black-soft">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid md:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-8 bg-lux-gold" />
                <span className="text-lux-gold text-xs font-medium tracking-[0.3em] uppercase">{tx.aboutTag}</span>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl font-light text-white mb-8 leading-tight">
                {tx.aboutTitle}<br />
                <span className="italic text-lux-pearl">{tx.aboutTitleAccent}</span>
              </h2>
              <p className="text-white/50 text-base leading-relaxed mb-5 font-light">
                {tx.aboutP1}
              </p>
              <p className="text-white/50 text-base leading-relaxed mb-10 font-light">
                {tx.aboutP2}
              </p>

              {/* Features grid - asymmetric bento style */}
              <div className="grid grid-cols-2 gap-3 mb-10">
                {tx.aboutFeatures.map((item) =>
                <div key={item} className="border border-white/10 px-4 py-3 hover:border-lux-gold/30 transition-colors duration-300">
                    <span className="text-white/70 text-sm font-light tracking-wide">{item}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-8">
                {tx.aboutStats.map((stat, i) =>
                <React.Fragment key={stat}>
                    {i > 0 && <div className="w-px h-8 bg-white/10" />}
                    <div>
                      <div className="text-white text-sm font-medium tracking-wide">{stat}</div>
                    </div>
                  </React.Fragment>
                )}
              </div>
            </div>

            <div className="relative">
              <div className="overflow-hidden">
                <img
                  src="/assets/images/navastudio.2025-1778-1783922237224.JPEG"
                  alt="Vista interior de un cenote con aguas cristalinas y estalactitas iluminadas naturalmente"
                  className="w-full h-[520px] object-cover grayscale hover:grayscale-0 transition-all duration-700"
                  loading="lazy"
                  decoding="async" />
                
              </div>
              {/* Floating card */}
              <div className="absolute -bottom-6 -left-6 bg-lux-black border border-white/10 p-5 shadow-2xl">
                <div className="text-xs text-white/40 tracking-widest uppercase mb-2">{tx.aboutAllies}</div>
                <div className="text-white font-light text-sm">Healing Spot</div>
                <div className="text-white font-light text-sm">Copal Gastrobar</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LAS CUATRO EXPERIENCIAS */}
      <section id="experiencias" className="py-24 md:py-32 bg-lux-black">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-8 bg-lux-gold" />
              <span className="text-lux-gold text-xs font-medium tracking-[0.3em] uppercase">{tx.expTag}</span>
            </div>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <h2 className="font-serif text-4xl md:text-5xl font-light text-white leading-tight">
                {tx.expTitle}
              </h2>
              <p className="text-white/40 text-sm max-w-xs font-light leading-relaxed">
                {tx.expSub}
              </p>
            </div>
          </div>

          {/* Bento-style grid with varied sizes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5">
            {experiences.map((exp, idx) =>
            <div
              key={exp.id}
              className={`relative group overflow-hidden bg-lux-black ${idx === 0 ? 'md:col-span-1' : ''}`}>
              
                <div className="relative h-72 md:h-80 overflow-hidden">
                  <img
                  src={exp.image}
                  alt={exp.alt}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                  loading="lazy"
                  decoding="async" />
                
                  <div className="absolute inset-0 bg-gradient-to-t from-lux-black via-lux-black/30 to-transparent" />
                  <div className="absolute top-5 left-5">
                    <span className="border border-white/30 text-white text-xs px-3 py-1 tracking-widest uppercase font-light">
                      {exp.badge[lang]}
                    </span>
                  </div>
                </div>

                <div className="p-7 border-t border-white/5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <span className="text-white/30 text-xs tracking-widest uppercase font-light block mb-1">
                        {exp.providerType[lang]} · {exp.provider}
                      </span>
                      <h3 className="font-serif text-xl text-white font-light">
                        {lang === 'es' ? exp.name : exp.nameEn}
                      </h3>
                    </div>
                  </div>

                  <p className="text-white/40 text-sm leading-relaxed mb-5 font-light">{exp.description[lang]}</p>

                  <div className="space-y-1.5 mb-6">
                    {exp.includes[lang].map((item) =>
                  <div key={item} className="flex items-center gap-2.5 text-xs text-white/40 font-light">
                        <div className="w-1 h-1 bg-lux-gold rounded-full flex-shrink-0" />
                        {item}
                      </div>
                  )}
                  </div>

                  <div className="flex items-end justify-between pt-5 border-t border-white/5">
                    <div>
                      <div className="text-white/30 text-xs tracking-widest uppercase mb-1">{tx.expFrom}</div>
                      <div className="text-white text-2xl font-light font-serif">
                        ${exp.priceBase.toLocaleString()}
                        <span className="text-sm text-white/30 ml-1 font-sans">MXN</span>
                      </div>
                      <div className="text-white/30 text-xs mt-0.5">{tx.expTransport} ${exp.priceTransport.toLocaleString()} MXN</div>
                    </div>
                    <button
                    onClick={() => openBooking(exp)}
                    className="border border-white/20 text-white hover:bg-white hover:text-lux-black px-6 py-2.5 text-xs font-medium tracking-[0.2em] uppercase transition-all duration-300">
                    
                      {tx.expBook}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="text-center mt-12">
            <p className="text-white/30 text-sm mb-5 font-light">{tx.expCustom}</p>
            <button
              onClick={() => openBooking()}
              className="border border-white/20 text-white hover:border-lux-gold hover:text-lux-gold px-10 py-3.5 text-xs font-medium tracking-[0.2em] uppercase transition-all duration-300">
              
              {tx.expCustomBtn}
            </button>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-24 md:py-32 bg-lux-black-soft">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-8 bg-lux-gold" />
              <span className="text-lux-gold text-xs font-medium tracking-[0.3em] uppercase">{tx.whyTag}</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-white leading-tight">
              {tx.whyTitle}
            </h2>
          </div>

          {/* Asymmetric layout - not a uniform grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5">
            {differentiators[lang].map((item, i) =>
            <div
              key={item.title}
              className={`bg-lux-black-soft p-8 hover:bg-lux-charcoal/30 transition-colors duration-300 ${i === 0 ? 'md:col-span-2' : ''} ${i === 3 ? 'md:col-span-2' : ''}`}>
              
                <div className="text-lux-gold text-xs tracking-[0.3em] uppercase font-medium mb-3">0{i + 1}</div>
                <h3 className="text-white font-serif text-xl font-light mb-3">{item.title}</h3>
                <p className="text-white/40 text-sm leading-relaxed font-light">{item.desc}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ADD-ONS */}
      <section className="py-24 bg-lux-black">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="mb-14">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-8 bg-lux-gold" />
              <span className="text-lux-gold text-xs font-medium tracking-[0.3em] uppercase">{tx.addonsTag}</span>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-light text-white mb-3">
              {tx.addonsTitle}
            </h2>
            <p className="text-white/40 max-w-md font-light text-sm leading-relaxed">
              {tx.addonsSub}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-white/5">
            {addons[lang].map((addon) =>
            <div key={addon.name} className="bg-lux-black p-6 text-center hover:bg-lux-black-soft transition-colors duration-300 group">
                <div className="font-serif text-white text-sm font-light mb-1 group-hover:text-lux-gold transition-colors duration-300">{addon.name}</div>
                <div className="text-white/30 text-xs mb-3 font-light">{addon.desc}</div>
                <div className="text-lux-gold text-xs font-medium tracking-wide">{addon.price}</div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* PROVIDERS */}
      <section className="py-24 bg-lux-black-soft">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="mb-14">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-8 bg-lux-gold" />
              <span className="text-lux-gold text-xs font-medium tracking-[0.3em] uppercase">{tx.providersTag}</span>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-light text-white">
              {tx.providersTitle}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-px bg-white/5">
            <div className="bg-lux-black-soft group overflow-hidden">
              <div className="relative h-52 overflow-hidden">
                <img
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_11c63cde5-1772488339861.png"
                  alt="Terapeuta de Healing Spot realizando un masaje relajante en un ambiente de bienestar natural"
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                  loading="lazy"
                  decoding="async" />
                
                <div className="absolute inset-0 bg-lux-black/50" />
                <div className="absolute inset-0 flex items-end p-6">
                  <h3 className="font-serif text-2xl font-light text-white">Healing Spot</h3>
                </div>
              </div>
              <div className="p-6 border-t border-white/5">
                <p className="text-white/40 text-sm leading-relaxed mb-4 font-light">
                  {lang === 'es' ? 'Especialistas en bienestar y terapias ancestrales mayas. Masajes terapéuticos, rituales de sanación y experiencias de wellness auténticas.' : 'Specialists in wellness and ancestral Mayan therapies. Therapeutic massages, healing rituals and authentic wellness experiences.'}
                </p>
                <div className="flex flex-wrap gap-2">
                  {(lang === 'es' ?
                  ['Terapia Maya', 'Deep Tissue', 'Masaje Relax', 'Ritual de Parejas'] :
                  ['Mayan Therapy', 'Deep Tissue', 'Relaxing Massage', 'Couples Ritual']).
                  map((tag) =>
                  <span key={tag} className="border border-white/10 text-white/40 text-xs px-3 py-1 font-light">{tag}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-lux-black-soft group overflow-hidden">
              <div className="relative h-52 overflow-hidden">
                <img
                  src="/assets/images/cgb_5-scaled-1783923805846.jpg"
                  alt="Chef de Copal Gastrobar preparando un plato gourmet con ingredientes locales mexicanos"
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                  loading="lazy"
                  decoding="async" />
                
                <div className="absolute inset-0 bg-lux-black/50" />
                <div className="absolute inset-0 flex items-end p-6">
                  <h3 className="font-serif text-2xl font-light text-white">Copal Gastrobar</h3>
                </div>
              </div>
              <div className="p-6 border-t border-white/5">
                <p className="text-white/40 text-sm leading-relaxed mb-4 font-light">
                  {lang === 'es' ? 'Gastronomía de autor en plena naturaleza. Desayunos exclusivos y cenas íntimas con ingredientes locales y técnicas culinarias de vanguardia.' : 'Signature cuisine in the heart of nature. Exclusive breakfasts and intimate dinners with local ingredients and avant-garde culinary techniques.'}
                </p>
                <div className="flex flex-wrap gap-2">
                  {(lang === 'es' ?
                  ['Desayuno Gourmet', 'Cena Íntima', 'Menú Cerrado', 'Carta Limitada'] :
                  ['Gourmet Breakfast', 'Intimate Dinner', 'Set Menu', 'Limited À La Carte']).
                  map((tag) =>
                  <span key={tag} className="border border-white/10 text-white/40 text-xs px-3 py-1 font-light">{tag}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="py-24 bg-lux-black">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="mb-14">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-8 bg-lux-gold" />
              <span className="text-lux-gold text-xs font-medium tracking-[0.3em] uppercase">{tx.reviewsTag}</span>
            </div>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <h2 className="font-serif text-3xl md:text-4xl font-light text-white">
                {tx.reviewsTitle}
              </h2>
              <div className="inline-flex items-center gap-2 border border-lux-gold/30 px-4 py-2">
                <span className="text-lux-gold text-xs font-medium tracking-widest">{tx.softOpening}</span>
                <span className="text-white/30 text-xs">{tx.softOpeningSub}</span>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-white/5 mb-12">
            {reviews.map((review) =>
            <div key={review.name} className="bg-lux-black p-8 hover:bg-lux-black-soft transition-colors duration-300">
                <div className="flex items-center gap-1 mb-5">
                  {Array.from({ length: review.rating }).map((_, i) =>
                <svg key={i} className="w-3 h-3 text-lux-gold fill-current" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                )}
                </div>
                <p className="text-white/50 text-sm leading-relaxed mb-6 italic font-light">&ldquo;{review.text[lang]}&rdquo;</p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 border border-white/20 flex items-center justify-center text-white/50 text-xs font-medium flex-shrink-0">
                    {review.avatar}
                  </div>
                  <div>
                    <div className="text-white text-sm font-light">{review.name}</div>
                    <div className="text-white/30 text-xs">{review.location} · {review.experience[lang]}</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="border border-white/10 p-10 text-center">
            <h3 className="font-serif text-2xl text-white font-light mb-3">{tx.softOpeningCta}</h3>
            <p className="text-white/40 mb-6 text-sm font-light">{tx.softOpeningCtaSub}</p>
            <button
              onClick={() => openBooking()}
              className="bg-white text-lux-black hover:bg-lux-pearl px-10 py-3.5 text-xs font-medium tracking-[0.25em] uppercase transition-all duration-300">
              
              {tx.softOpeningBtn}
            </button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-lux-black-soft">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="mb-14">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-8 bg-lux-gold" />
              <span className="text-lux-gold text-xs font-medium tracking-[0.3em] uppercase">{tx.faqTag}</span>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-light text-white">
              {tx.faqTitle}
            </h2>
          </div>

          <div className="divide-y divide-white/5">
            {faqs[lang].map((faq, i) =>
            <div key={i}>
                <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full flex items-center justify-between py-5 text-left group">
                
                  <span className="text-white/70 text-sm font-light pr-6 group-hover:text-white transition-colors duration-200">{faq.q}</span>
                  <svg
                  className={`w-4 h-4 text-lux-gold flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openFaq === i &&
              <div className="pb-5">
                    <p className="text-white/40 text-sm leading-relaxed font-light">{faq.a}</p>
                  </div>
              }
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer id="contacto" className="bg-lux-black border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
          <div className="grid md:grid-cols-3 gap-12 mb-14">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-6 h-6 border border-lux-gold flex items-center justify-center">
                  <svg viewBox="0 0 24 24" fill="none" className="w-3.5 h-3.5 text-lux-gold" stroke="currentColor" strokeWidth="1">
                    <path d="M12 3C9 3 5 6.5 5 10.5c0 5 7 11 7 11s7-6 7-11C19 6.5 15 3 12 3z" />
                    <circle cx="12" cy="10" r="2.5" fill="currentColor" />
                  </svg>
                </div>
                <span className="font-serif text-white text-sm font-light tracking-[0.2em] uppercase">Cenote Privado</span>
              </div>
              <p className="text-white/30 text-sm leading-relaxed mb-5 font-light">
                {tx.footerDesc}
              </p>
            </div>

            <div>
              <h4 className="text-white/30 text-xs tracking-[0.25em] uppercase font-medium mb-5">{tx.footerExp}</h4>
              <ul className="space-y-2.5">
                {(lang === 'es' ?
                ['Desayuno en el Cenote', 'Masaje en el Cenote', 'Cena en el Cenote', 'Ritual Maya'] :
                ['Cenote Breakfast', 'Cenote Massage', 'Cenote Dinner', 'Mayan Ritual']).
                map((item) =>
                <li key={item}>
                    <button
                    onClick={() => scrollTo('experiencias')}
                    className="text-white/30 hover:text-white text-sm font-light transition-colors duration-200">
                    
                      {item}
                    </button>
                  </li>
                )}
              </ul>
            </div>

            <div>
              <h4 className="text-white/30 text-xs tracking-[0.25em] uppercase font-medium mb-5">{tx.footerContact}</h4>
              <div className="space-y-3">
                <a
                  href="https://wa.me/5215592252233"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-white/30 hover:text-white transition-colors duration-200 group">
                  
                  <div className="w-7 h-7 border border-white/10 group-hover:border-white/30 flex items-center justify-center flex-shrink-0 transition-colors duration-200">
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                  </div>
                  <span className="text-sm font-light">+52 55 9225 2233</span>
                </a>
                <a
                  href="mailto:reservas@cenoteprivado.mx"
                  className="flex items-center gap-3 text-white/30 hover:text-white transition-colors duration-200 group">
                  
                  <div className="w-7 h-7 border border-white/10 group-hover:border-white/30 flex items-center justify-center flex-shrink-0 transition-colors duration-200">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <span className="text-sm font-light">reservas@cenoteprivado.mx</span>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/20 text-xs font-light">{tx.footerRights}</p>
            <p className="text-white/20 text-xs tracking-[0.2em] font-light">{tx.footerTagline}</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/5215592252233"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-40 w-12 h-12 bg-white hover:bg-lux-pearl flex items-center justify-center shadow-2xl transition-all duration-300 hover:-translate-y-0.5"
        aria-label="WhatsApp">
        
        <svg className="w-5 h-5 text-lux-black" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      </a>

      <BookingModal
        isOpen={bookingOpen}
        onClose={() => setBookingOpen(false)}
        selectedExperience={selectedExp}
        lang={lang} />
      
    </div>);

}